import React, { useEffect, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { personalInfo } from '../data/mock';
import { Code, Database, Smartphone, BarChart3, Zap, Target } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const About = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const iconsRef = useRef(null);
  const statsRef = useRef(null);
  const { currentColors } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Enhanced title animation with 3D effect
            anime({
              targets: titleRef.current,
              opacity: [0, 1],
              translateY: [80, 0],
              rotateX: [90, 0],
              scale: [0.8, 1.05, 1],
              duration: 1200,
              easing: 'easeOutElastic(1, .6)'
            });

            // Content animation with stagger for paragraphs
            const contentElements = contentRef.current.children;
            anime({
              targets: contentElements,
              opacity: [0, 1],
              translateY: [40, 0],
              translateX: [-20, 0],
              duration: 1000,
              delay: anime.stagger(200, { start: 400 }),
              easing: 'easeOutQuart'
            });

            // Enhanced icons animation with rotation and glow
            anime({
              targets: iconsRef.current.children,
              opacity: [0, 1],
              scale: [0.5, 1.2, 1],
              rotateY: [180, 0],
              duration: 800,
              delay: anime.stagger(200, { start: 800 }),
              easing: 'easeOutElastic(1, .8)'
            });

            // Add hover animations for icons
            Array.from(iconsRef.current.children).forEach((icon) => {
              icon.addEventListener('mouseenter', () => {
                anime({
                  targets: icon,
                  scale: [1, 1.15],
                  rotateY: [0, 10],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              });
              
              icon.addEventListener('mouseleave', () => {
                anime({
                  targets: icon,
                  scale: [1.15, 1],
                  rotateY: [10, 0],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              });
            });

            // Stats counter animation
            if (statsRef.current) {
              const statNumbers = statsRef.current.querySelectorAll('.stat-number');
              statNumbers.forEach((stat, index) => {
                const finalValue = stat.textContent;
                stat.textContent = '0';
                
                anime({
                  targets: stat,
                  innerHTML: [0, parseInt(finalValue)],
                  duration: 2000,
                  delay: 1500 + (index * 300),
                  round: 1,
                  easing: 'easeOutQuart'
                });
              });

              // Stats container animation
              anime({
                targets: statsRef.current.children,
                opacity: [0, 1],
                translateY: [30, 0],
                scale: [0.9, 1],
                duration: 600,
                delay: anime.stagger(200, { start: 1400 }),
                easing: 'easeOutQuart'
              });
            }

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const specialties = [
    { icon: <Database size={28} />, label: "Data Analysis", color: '#FF6B6B' },
    { icon: <Code size={28} />, label: "Web Development", color: '#4ECDC4' },
    { icon: <Smartphone size={28} />, label: "Cross Platform", color: '#45B7D1' },
    { icon: <Zap size={28} />, label: "Optimization", color: '#FFA07A' }
  ];

  return (
    <section 
      id="about" 
      ref={sectionRef} 
      className="py-16 md:py-32 relative z-10 transition-colors duration-500"
      style={{ backgroundColor: currentColors.cardBg }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column - Text Content */}
          <div>
            <h2 
              ref={titleRef}
              className="text-4xl md:text-6xl font-bold uppercase tracking-tight mb-12 opacity-0 relative font-display"
              style={{ 
                color: currentColors.text
              }}
            >
              About Me
              <div 
                className="absolute -bottom-2 left-0 h-1 w-20 opacity-80"
                style={{ backgroundColor: currentColors.accent }}
              />
            </h2>

            <div ref={contentRef}>
              <p 
                className="text-lg leading-relaxed mb-8 opacity-0"
                style={{ color: currentColors.text }}
              >
                {personalInfo.description}
              </p>
              
              <p 
                className="text-base leading-relaxed mb-12 opacity-0"
                style={{ color: currentColors.textSecondary }}
              >
                I thrive at the intersection of data science and software development, 
                creating solutions that not only process and analyze complex datasets 
                but also deliver intuitive user experiences across web and mobile platforms.
              </p>

              {/* Specialties Icons */}
              <div ref={iconsRef} className="grid grid-cols-2 gap-4 md:gap-6">
                {specialties.map((specialty, index) => (
                  <div 
                    key={index}
                    className="flex flex-col items-center p-4 md:p-6 rounded-lg transition-all duration-500 opacity-0 cursor-pointer group relative overflow-hidden"
                    style={{ 
                      border: `2px solid ${currentColors.borderLight}`,
                      backgroundColor: `${currentColors.bg}80`
                    }}
                  >
                    <div 
                      className="mb-2 md:mb-3 p-2 md:p-3 rounded-full transition-all duration-300 group-hover:scale-110"
                      style={{ 
                        backgroundColor: `${specialty.color}20`,
                        color: specialty.color
                      }}
                    >
                      {specialty.icon}
                    </div>
                    <span 
                      className="font-mono text-xs uppercase tracking-wide text-center font-semibold"
                      style={{ color: currentColors.text }}
                    >
                      {specialty.label}
                    </span>
                    
                    {/* Hover glow effect */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"
                      style={{ 
                        background: `linear-gradient(45deg, ${specialty.color}10, transparent)`
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Info Card */}
          <div>
            {/* Info Card */}
            <div 
              className="about-info-card p-8 rounded-xl transition-all duration-300 hover:scale-105 cursor-pointer relative overflow-hidden"
              style={{ 
                backgroundColor: `${currentColors.accent}15`,
                border: `2px solid ${currentColors.accent}30`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = currentColors.accent;
                e.currentTarget.style.backgroundColor = `${currentColors.accent}20`;
                anime({
                  targets: e.currentTarget.querySelector('.info-icon'),
                  scale: [1, 1.3, 1.1],
                  rotate: [0, 360],
                  duration: 600,
                  easing: 'easeOutElastic(1, .8)'
                });
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = `${currentColors.accent}30`;
                e.currentTarget.style.backgroundColor = `${currentColors.accent}15`;
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Zap size={24} style={{ color: currentColors.accent }} className="info-icon" />
                <h3 
                  className="font-mono text-lg uppercase tracking-wide font-bold"
                  style={{ color: currentColors.text }}
                >
                  Innovation Driven
                </h3>
              </div>
              <p 
                className="text-base leading-relaxed"
                style={{ color: currentColors.textSecondary }}
              >
                Passionate about building scalable, performant applications that solve real-world problems. 
                From data analysis to cross-platform development, I bring creative solutions to complex challenges.
              </p>
              {/* Animated corner accent */}
              <div 
                className="absolute top-0 right-0 w-20 h-20 opacity-20"
                style={{ 
                  background: `radial-gradient(circle at top right, ${currentColors.accent}, transparent)`
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;